import { useMemo, useState } from "react"
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import { Link } from "react-router-dom"
import {
  useBudgetRecommenderControllerGetBrandedSearchQuery,
  type BrandedSearchBudgetDto,
} from "~/ui-rtk/api/budgetRecommenderApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button, Input } from "~/ui-rtk/components/ui/controls"
import {
  splitBrandedSearchDailyBudget,
  type BrandedSearchLadderRow,
  type BrandedSearchTier,
} from "~/shared/budget-recommender/branded-search"
import { formatNumber } from "~/ui-rtk/utils/format"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { formatSearchImpressionShare } from "../budget-utils"
import {
  BRAND_SEARCH_INITIATIVE_TYPE,
  type ApplyResult,
  type RecommendationRow,
} from "../types"

const GOOGLE_CHANNELS = new Set(["google_ads", "youtube_ads"])

/** The rows the branded-search budget is set on: BRANDED_SEARCH-typed Google campaigns. */
export function isBrandedSearchRow(row: RecommendationRow): boolean {
  return (
    row.entityType === "campaign" &&
    row.initiativeType === BRAND_SEARCH_INITIATIVE_TYPE &&
    GOOGLE_CHANNELS.has(row.channel)
  )
}

const TH =
  "border-b border-basic-blue px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-system-gray-400"
const TD =
  "border-b border-white/5 px-2 py-1 align-middle text-[12px] text-system-gray-100"
const NUM = "text-right tabular-nums"

const money = (value: number | null | undefined, decimals?: number) =>
  value === null || value === undefined
    ? "—"
    : formatNumber(value, { currency: true, decimals })
const pct = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : `${(value * 100).toFixed(1)}%`
const int = (value: number | null | undefined) =>
  value === null || value === undefined
    ? "—"
    : formatNumber(value, { decimals: 0 })
const signed = (value: number, format: (v: number) => string) => {
  if (value > 0) return `+${format(value)}`
  if (value < 0) return `−${format(-value)}`
  return format(0)
}

const LADDER_KIND_LABEL: Record<BrandedSearchLadderRow["kind"], string> = {
  "organic-only": "No paid",
  level: "",
  current: "Current",
  recommended: "Recommended",
}

const CONFIDENCE_CLASS = {
  high: "bg-states-success/20 text-states-success",
  medium: "bg-states-warn/20 text-states-warn",
  low: "bg-states-error/20 text-states-error",
} as const

interface BrandedSearchBudgetPanelProps {
  /** Every recommendation row; the panel picks the BRANDED_SEARCH Google campaigns */
  rows: RecommendationRow[]
  /** Writes the given rows at the override budgets through the page's approve path */
  onApply: (
    rows: RecommendationRow[],
    overrides: Map<string, number>,
  ) => Promise<ApplyResult[]>
  settingsLink?: string
}

function TierTable({
  title,
  tiers,
}: {
  title: string
  tiers: BrandedSearchTier[]
}) {
  return (
    <div className="overflow-x-auto">
      <h4 className="text-white text-xs font-medium mb-2">{title}</h4>
      {tiers.length === 0 ? (
        <p className="text-gray-400 text-xs">
          No weeks with Search Console data.
        </p>
      ) : (
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={TH}>Tier</th>
              <th className={cn(TH, NUM)}>Wks</th>
              <th className={cn(TH, NUM)}>Spend / wk</th>
              <th className={cn(TH, NUM)}>Paid clicks</th>
              <th className={cn(TH, NUM)}>Organic clicks</th>
              <th className={cn(TH, NUM)}>Branded CTR</th>
              <th
                className={cn(TH, NUM)}
                title="Δ spend ÷ Δ total clicks against the tier below"
              >
                $ / marginal click
              </th>
              <th
                className={cn(TH, NUM)}
                title="The same read with demand held at this tier's impressions"
              >
                demand-adjusted
              </th>
            </tr>
          </thead>
          <tbody>
            {tiers.map(tier => (
              <tr key={tier.tier}>
                <td className={TD}>{tier.tier}</td>
                <td className={cn(TD, NUM)}>{tier.weeks}</td>
                <td className={cn(TD, NUM)}>{money(tier.paidSpend, 0)}</td>
                <td className={cn(TD, NUM)}>{int(tier.paidClicks)}</td>
                <td className={cn(TD, NUM)}>{int(tier.organicClicks)}</td>
                <td className={cn(TD, NUM)}>{pct(tier.ctr)}</td>
                <td className={cn(TD, NUM)}>
                  {money(tier.costPerMarginalClick)}
                </td>
                <td className={cn(TD, NUM)}>
                  {money(tier.costPerMarginalClickAdjusted)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

interface ApplyDialogProps {
  isOpen: boolean
  data: BrandedSearchBudgetDto | undefined
  plan: {
    writable: ReturnType<typeof splitBrandedSearchDailyBudget>
    unfunded: ReturnType<typeof splitBrandedSearchDailyBudget>
    offPage: ReturnType<typeof splitBrandedSearchDailyBudget>
    notWritable: ReturnType<typeof splitBrandedSearchDailyBudget>
  } | null
  planRow: BrandedSearchLadderRow | null
  planWeeklySpend: number | null
  rowByCampaignId: Map<string, RecommendationRow>
  isApplying: boolean
  onCancel: () => void
  onConfirm: () => void
}

function ApplyDialog({
  isOpen,
  data,
  plan,
  planRow,
  planWeeklySpend,
  rowByCampaignId,
  isApplying,
  onCancel,
  onConfirm,
}: ApplyDialogProps) {
  // The window every campaign's Search impression share covers (one live
  // read, one window); null when no campaign on the plan carries a share.
  const impressionShareWindow =
    plan?.writable
      .map(entry => rowByCampaignId.get(entry.campaignId)?.googleBidding)
      .find(
        bidding =>
          formatSearchImpressionShare(bidding) !== null &&
          bidding?.impressionShareWindow,
      )?.impressionShareWindow ?? null
  return (
    <Dialog
      isOpen={isOpen}
      handleClose={onCancel}
      width={640}
      classes={{
        container:
          "bg-basic-dark-blue border border-basic-blue rounded-xl p-0 text-left max-h-[85vh] flex flex-col",
      }}
    >
      <div className="px-6 py-4 border-b border-basic-blue">
        <h2 className="text-lg font-semibold text-white">
          Set branded search to{" "}
          {money(planWeeklySpend === null ? null : planWeeklySpend / 7, 0)}/day
        </h2>
        {planRow && (
          <p className="text-gray-400 text-xs mt-1">
            {money(planRow.weeklySpend, 0)}/wk · expected branded CTR{" "}
            {pct(planRow.ctr)} · {int(planRow.expectedClicks)} clicks/wk · next
            click {money(planRow.costPerMarginalClick)}
            {planRow.extrapolated &&
              " · above the spend observed in the last 52 weeks"}
          </p>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        <p className="text-sm text-system-gray-200">
          Split pro-rata on each campaign&apos;s trailing four weeks of spend.
          Each budget is written to Google as a daily budget, like an approve
          from the table.
        </p>
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={TH}>Campaign</th>
              <th className={cn(TH, NUM)}>Share</th>
              <th className={cn(TH, NUM)}>Current / day</th>
              <th className={cn(TH, NUM)}>New / day</th>
            </tr>
          </thead>
          <tbody>
            {plan?.writable.map(entry => {
              const row = rowByCampaignId.get(entry.campaignId)
              const impressionShare = formatSearchImpressionShare(
                row?.googleBidding,
              )
              return (
                <tr key={entry.campaignId}>
                  <td className={TD}>
                    {entry.campaignName}
                    {impressionShare && (
                      <div
                        className="text-[11px] text-gray-400"
                        data-testid="branded-search-impression-share"
                      >
                        {impressionShare}
                      </div>
                    )}
                  </td>
                  <td className={cn(TD, NUM)}>{pct(entry.share)}</td>
                  <td className={cn(TD, NUM)}>
                    {money(row?.currentDailyBudget, 0)}
                  </td>
                  <td className={cn(TD, NUM, "font-semibold text-white")}>
                    {money(entry.dailyBudgetCents / 100)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {impressionShareWindow && (
          <p className="text-xs text-gray-400">
            IS is Google&apos;s Search impression share for{" "}
            {impressionShareWindow.since} to {impressionShareWindow.until}: the
            share of eligible searches the campaign showed on, and what it lost
            to budget and to rank. Share lost to budget is what more budget can
            buy; share lost to rank is not.
          </p>
        )}
        {plan && plan.unfunded.length > 0 && (
          <p className="text-xs text-gray-400">
            Not funded (no spend in the trailing four weeks, left as is):{" "}
            {plan.unfunded.map(c => c.campaignName).join(", ")}.
          </p>
        )}
        {plan && plan.offPage.length > 0 && (
          <p className="text-xs text-states-warn">
            Not on this page, so not written:{" "}
            {plan.offPage
              .map(
                c =>
                  `${c.campaignName} (${money(c.dailyBudgetCents / 100)}/day)`,
              )
              .join(", ")}
            .
          </p>
        )}
        {plan && plan.notWritable.length > 0 && (
          <p className="text-xs text-states-warn">
            Cannot be written from here (shared budget or no platform write), so
            left as is:{" "}
            {plan.notWritable
              .map(
                c =>
                  `${c.campaignName} (would be ${money(c.dailyBudgetCents / 100)}/day)`,
              )
              .join(", ")}
            .
          </p>
        )}
        {data?.freshnessUnavailable && (
          <p className="text-xs text-states-warn">
            Data freshness tracking is unavailable; the model assumed data
            through {data.dataThrough}.
          </p>
        )}
      </div>
      <div className="px-6 py-4 border-t border-basic-blue flex justify-end gap-2">
        <Button
          variant={{ variant: "outlined" }}
          size="sm"
          type="button"
          onClick={onCancel}
          disabled={isApplying}
        >
          Cancel
        </Button>
        <Button
          variant={{ variant: "solid", color: "pink" }}
          size="sm"
          type="button"
          onClick={onConfirm}
          disabled={isApplying || !plan || plan.writable.length === 0}
        >
          {isApplying ? "Applying…" : "Apply budgets"}
        </Button>
      </div>
    </Dialog>
  )
}

/**
 * The branded-search budget: what weekly paid branded-search spend buys in
 * total branded clicks (paid + organic) at current demand, the cost of the
 * next click at each level, and the level where it meets the target. The
 * model is computed server-side (GET /budget-recommender/branded-search, the
 * same code the MCP's branded_search_budget reads); this panel shows the
 * ladder and applies a chosen level across the typed campaigns pro-rata on
 * trailing spend. BUDGET_RECOMMENDER.md ("Branded-search budget") has the
 * method.
 */
export default function BrandedSearchBudgetPanel({
  rows,
  onApply,
  settingsLink,
}: BrandedSearchBudgetPanelProps) {
  const brandRows = useMemo(() => rows.filter(isBrandedSearchRow), [rows])
  const rowByCampaignId = useMemo(
    () => new Map(brandRows.map(r => [r.campaignId, r])),
    [brandRows],
  )

  const [targetInput, setTargetInput] = useState("")
  const [appliedTarget, setAppliedTarget] = useState<number | undefined>()
  const [showDetails, setShowDetails] = useState(false)
  // A ladder level the user picked instead of the recommendation.
  const [chosenWeeklySpend, setChosenWeeklySpend] = useState<number | null>(
    null,
  )
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const [applyMessage, setApplyMessage] = useState<string | null>(null)

  const { data, isLoading, isFetching, error, refetch } =
    useBudgetRecommenderControllerGetBrandedSearchQuery(
      appliedTarget !== undefined
        ? { targetCostPerMarginalClick: appliedTarget }
        : undefined,
      { skip: brandRows.length === 0 },
    )

  const model = data?.model
  const planWeeklySpend =
    chosenWeeklySpend ?? model?.recommendation?.weeklySpend ?? null
  const planRow = useMemo(() => {
    if (!model || planWeeklySpend === null) return null
    return (
      model.ladder.find(r => Math.abs(r.weeklySpend - planWeeklySpend) < 0.5) ??
      null
    )
  }, [model, planWeeklySpend])

  // The split of the planned weekly spend across the typed campaigns, with
  // each campaign's live daily budget from the page's rows.
  const plan = useMemo(() => {
    if (!data || planWeeklySpend === null) return null
    const split = splitBrandedSearchDailyBudget(
      planWeeklySpend,
      data.campaigns.map(c => {
        const row = rowByCampaignId.get(c.campaignId)
        return {
          ...c,
          currentDailyBudgetCents: row
            ? Math.round(row.currentDailyBudget * 100)
            : null,
        }
      }),
    )
    const writable = split.filter(s => {
      const row = rowByCampaignId.get(s.campaignId)
      return s.dailyBudgetCents > 0 && row?.canApplyBudget
    })
    const unfunded = split.filter(s => s.dailyBudgetCents === 0)
    const offPage = split.filter(
      s => s.dailyBudgetCents > 0 && !rowByCampaignId.get(s.campaignId),
    )
    // On the page but not writable from here (the table shows these "as is"
    // too): a shared Google budget, or a platform the API cannot write.
    const notWritable = split.filter(s => {
      const row = rowByCampaignId.get(s.campaignId)
      return s.dailyBudgetCents > 0 && row !== undefined && !row.canApplyBudget
    })
    return { split, writable, unfunded, offPage, notWritable }
  }, [data, planWeeklySpend, rowByCampaignId])

  if (brandRows.length === 0) return null

  const commitTarget = () => {
    const value = Number(targetInput)
    setChosenWeeklySpend(null)
    setAppliedTarget(
      targetInput.trim() !== "" && Number.isFinite(value) && value > 0
        ? value
        : undefined,
    )
  }
  const resetTarget = () => {
    setTargetInput("")
    setAppliedTarget(undefined)
    setChosenWeeklySpend(null)
  }

  const handleConfirm = async () => {
    if (!plan || plan.writable.length === 0) return
    setIsApplying(true)
    try {
      const overrides = new Map<string, number>()
      const rowsToWrite: RecommendationRow[] = []
      for (const entry of plan.writable) {
        const row = rowByCampaignId.get(entry.campaignId)
        if (!row) continue
        rowsToWrite.push(row)
        overrides.set(row.entityId, entry.dailyBudgetCents / 100)
      }
      const results = await onApply(rowsToWrite, overrides)
      const failed = results.filter(r => !r.success)
      setApplyMessage(
        failed.length === 0
          ? `Updated ${results.length} branded-search budget${results.length === 1 ? "" : "s"} to ${money(planWeeklySpend === null ? null : planWeeklySpend / 7)}/day combined.`
          : `${results.length - failed.length} of ${results.length} updated. Failed: ${failed
              .map(r => `${r.entityName}${r.error ? ` (${r.error})` : ""}`)
              .join("; ")}`,
      )
      setIsConfirmOpen(false)
      setChosenWeeklySpend(null)
      void refetch()
    } finally {
      setIsApplying(false)
    }
  }

  const targetSourceLabel = (() => {
    if (!model) return ""
    switch (model.target.source) {
      case "user":
        return "custom target"
      case "target-mer":
        return `value per session ÷ ${data?.guardrail.display === "cac" ? "guardrail" : (data?.guardrail.display ?? "guardrail").toUpperCase()} ${model.target.targetMer ?? ""}`
      case "break-even":
        return "break-even on value per session (no spend guardrail set)"
      default:
        return "no value-per-session data; set a target to get a recommendation"
    }
  })()

  return (
    <div className="bg-system-blue-800 rounded-lg p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-white font-medium text-sm">
              Branded search budget
            </h3>
            {model?.confidence && (
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                  CONFIDENCE_CLASS[model.confidence],
                )}
                title={
                  model.curve
                    ? `R² ${model.curve.r2.toFixed(2)} over ${model.curve.weeks} weeks; spend observed ${money(model.curve.spendMin, 0)}–${money(model.curve.spendMax, 0)}/wk`
                    : undefined
                }
              >
                {model.confidence} confidence
              </span>
            )}
          </div>
          <p className="text-gray-400 text-xs mt-1 max-w-3xl">
            What weekly paid branded-search spend buys in total branded clicks
            (paid + organic ÷ Search Console branded impressions) at current
            demand, and the spend where the next click costs the target.
            {data && (
              <>
                {" "}
                Data through {data.dataThrough}, {data.weekStartDay}-start
                weeks, {brandRows.length} typed campaign
                {brandRows.length === 1 ? "" : "s"}
                {data.brandSearchPolicy === "optimize" &&
                  "; the brand-search policy is Optimize, so the allocation above also moves these rows"}
                .
              </>
            )}
          </p>
        </div>
        {settingsLink && (
          <Link
            to={settingsLink}
            className="text-xs text-system-brand-pink-400 hover:text-system-brand-pink-300 underline transition-colors shrink-0"
          >
            Spend guardrail and brand-search policy
          </Link>
        )}
      </div>

      {isLoading && (
        <div className="grid grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-1">
              <div className="h-4 w-24 bg-system-blue-600/40 rounded animate-pulse" />
              <div className="h-10 bg-system-blue-600/40 rounded animate-pulse" />
            </div>
          ))}
        </div>
      )}
      {!isLoading && (error || !data || !model) && (
        <p className="text-states-error text-sm">
          The branded-search model could not be loaded.
        </p>
      )}
      {!isLoading && data && model && (
        <div className={cn("space-y-4", isFetching && "opacity-70")}>
          {model.status !== "ok" && (
            <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg px-4 py-3 text-yellow-300 text-sm">
              {model.status === "insufficient-history"
                ? `Not enough history to fit a curve: ${model.reason} Branded organic data needs Search Console connected and brand terms set on the company.`
                : model.reason}
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="text-gray-400 text-xs block mb-1">
                Current (trailing {model.current.weeks} wks)
              </label>
              <div className="text-white text-lg font-semibold">
                {money(model.current.weeklySpend, 0)}
                <span className="text-gray-400 text-xs font-normal">/wk</span>
              </div>
              <div className="text-gray-400 text-xs">
                {money(model.current.dailyBudget, 0)}/day · CTR{" "}
                {pct(model.current.observedCtr)} ·{" "}
                {int(model.current.totalClicks)} clicks/wk
              </div>
            </div>

            <div>
              <label className="text-gray-400 text-xs block mb-1">
                Recommended
              </label>
              {model.recommendation ? (
                <>
                  <div className="text-white text-lg font-semibold">
                    {money(model.recommendation.weeklySpend, 0)}
                    <span className="text-gray-400 text-xs font-normal">
                      /wk
                    </span>
                  </div>
                  <div className="text-gray-400 text-xs">
                    {money(model.recommendation.dailyBudget, 0)}/day ·{" "}
                    {signed(model.recommendation.incrementalSpendVsCurrent, v =>
                      money(v, 0),
                    )}{" "}
                    vs current ·{" "}
                    {signed(
                      model.recommendation.incrementalClicksVsCurrent,
                      int,
                    )}{" "}
                    clicks/wk
                    {model.recommendation.extrapolated &&
                      " · above the spend observed this year"}
                    {model.recommendation.capped && " · capped"}
                  </div>
                </>
              ) : (
                <div className="text-gray-400 text-sm">—</div>
              )}
            </div>

            <div>
              <label className="text-gray-400 text-xs block mb-1">
                Target cost per marginal click
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={targetInput}
                  placeholder={
                    model.target.costPerMarginalClick === null
                      ? "e.g. 3.00"
                      : model.target.costPerMarginalClick.toFixed(2)
                  }
                  onChange={e => setTargetInput(e.target.value)}
                  onBlur={commitTarget}
                  onKeyDown={e => {
                    if (e.key === "Enter") commitTarget()
                  }}
                  step="0.25"
                  min="0"
                  className="w-28"
                />
                {appliedTarget !== undefined && (
                  <Button
                    variant={{ variant: "outlined" }}
                    size="sm"
                    type="button"
                    onClick={resetTarget}
                  >
                    Reset
                  </Button>
                )}
              </div>
              <div className="text-gray-400 text-xs mt-1">
                {money(model.target.costPerMarginalClick)} · {targetSourceLabel}
              </div>
            </div>

            <div>
              <label className="text-gray-400 text-xs block mb-1">
                Value per branded session
              </label>
              <div className="text-white text-lg font-semibold">
                {money(model.value.perSession)}
              </div>
              <div className="text-gray-400 text-xs">
                organic only {money(model.value.organicPerSession)} · paid
                revenue/click {money(model.value.paidRevenuePerClick)} ·{" "}
                {model.value.weeksUsed} wks
              </div>
            </div>
          </div>

          {model.ladder.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className={TH}>Weekly spend</th>
                    <th className={cn(TH, NUM)}>Daily</th>
                    <th className={cn(TH, NUM)}>Branded CTR</th>
                    <th className={cn(TH, NUM)}>Clicks / wk</th>
                    <th className={cn(TH, NUM)}>Δ clicks vs current</th>
                    <th className={cn(TH, NUM)}>Cost / marginal click</th>
                    <th className={cn(TH, NUM)}>Marginal MER</th>
                    <th className={TH} />
                  </tr>
                </thead>
                <tbody>
                  {model.ladder.map(row => {
                    const isPlan =
                      planWeeklySpend !== null &&
                      Math.abs(row.weeklySpend - planWeeklySpend) < 0.5
                    const label = LADDER_KIND_LABEL[row.kind]
                    return (
                      <tr
                        key={`${row.kind}-${row.weeklySpend}`}
                        className={cn(
                          isPlan && "bg-white/5",
                          row.extrapolated && "text-system-gray-400",
                        )}
                        title={
                          row.extrapolated
                            ? "Above the highest weekly spend observed in the last 52 weeks"
                            : undefined
                        }
                      >
                        <td
                          className={cn(
                            TD,
                            isPlan && "font-semibold text-white",
                          )}
                        >
                          {money(row.weeklySpend, 0)}
                          {label && (
                            <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-system-gray-200">
                              {label}
                            </span>
                          )}
                          {row.extrapolated && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide">
                              beyond observed
                            </span>
                          )}
                        </td>
                        <td className={cn(TD, NUM)}>
                          {money(row.dailyBudget, 0)}
                        </td>
                        <td className={cn(TD, NUM)}>{pct(row.ctr)}</td>
                        <td className={cn(TD, NUM)}>
                          {int(row.expectedClicks)}
                        </td>
                        <td className={cn(TD, NUM)}>
                          {signed(row.incrementalClicksVsCurrent, int)}
                        </td>
                        <td className={cn(TD, NUM)}>
                          {money(row.costPerMarginalClick)}
                        </td>
                        <td className={cn(TD, NUM)}>
                          {row.marginalMer === null
                            ? "—"
                            : `${row.marginalMer.toFixed(1)}x`}
                        </td>
                        <td className={cn(TD, "text-right")}>
                          {row.weeklySpend > 0 && (
                            <button
                              type="button"
                              className={cn(
                                "text-xs underline",
                                isPlan
                                  ? "text-white"
                                  : "text-system-brand-pink-400 hover:text-system-brand-pink-300",
                              )}
                              onClick={() =>
                                setChosenWeeklySpend(
                                  row.kind === "recommended"
                                    ? null
                                    : row.weeklySpend,
                                )
                              }
                            >
                              {isPlan ? "Selected" : "Use"}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-system-gray-300 hover:text-white"
              onClick={() => setShowDetails(v => !v)}
            >
              {showDetails ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )}
              What each spend level bought, and the same weeks last year
            </button>
            <div className="flex items-center gap-3">
              {applyMessage && (
                <span className="text-xs text-system-gray-300">
                  {applyMessage}
                </span>
              )}
              <Button
                variant={{ variant: "solid", color: "pink" }}
                size="sm"
                type="button"
                disabled={!plan || plan.writable.length === 0}
                onClick={() => setIsConfirmOpen(true)}
              >
                {planWeeklySpend === null
                  ? "No level to apply"
                  : `Apply ${money(planWeeklySpend / 7, 0)}/day across ${plan?.writable.length ?? 0} campaign${plan?.writable.length === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>

          {showDetails && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 pt-2">
              <TierTable title="Last 52 weeks by spend" tiers={model.tiers} />
              <TierTable
                title="The year before, by spend"
                tiers={model.priorTiers}
              />
              <div className="xl:col-span-2 overflow-x-auto">
                <h4 className="text-white text-xs font-medium mb-2">
                  Newest weeks vs the same weeks last year
                </h4>
                <table className="w-full border-separate border-spacing-0">
                  <thead>
                    <tr>
                      <th className={TH}>Week</th>
                      <th className={cn(TH, NUM)}>Spend</th>
                      <th className={cn(TH, NUM)}>LY spend</th>
                      <th className={cn(TH, NUM)}>Branded CTR</th>
                      <th className={cn(TH, NUM)}>LY CTR</th>
                      <th className={cn(TH, NUM)}>Total clicks</th>
                      <th className={cn(TH, NUM)}>LY clicks</th>
                      <th className={cn(TH, NUM)}>Branded impressions</th>
                      <th className={cn(TH, NUM)}>LY impressions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {model.yoy.map(({ current, lastYear }) => (
                      <tr key={current.weekStart}>
                        <td className={TD}>{current.weekStart}</td>
                        <td className={cn(TD, NUM)}>
                          {money(current.paidSpend, 0)}
                        </td>
                        <td className={cn(TD, NUM)}>
                          {money(lastYear?.paidSpend, 0)}
                        </td>
                        <td className={cn(TD, NUM)}>{pct(current.ctr)}</td>
                        <td className={cn(TD, NUM)}>{pct(lastYear?.ctr)}</td>
                        <td className={cn(TD, NUM)}>
                          {int(current.totalClicks)}
                        </td>
                        <td className={cn(TD, NUM)}>
                          {int(lastYear?.totalClicks)}
                        </td>
                        <td className={cn(TD, NUM)}>
                          {int(current.organicImpressions)}
                        </td>
                        <td className={cn(TD, NUM)}>
                          {int(lastYear?.organicImpressions)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <ApplyDialog
        isOpen={isConfirmOpen}
        data={data}
        plan={plan}
        planRow={planRow}
        planWeeklySpend={planWeeklySpend}
        rowByCampaignId={rowByCampaignId}
        isApplying={isApplying}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirm}
      />
    </div>
  )
}
